import { DataVendors, VerificationNames } from "shared/types";
import { CreditReport } from "types/landlord/reports";
import { ReportCompletedBanner } from "views/applications-id-report/components/ReportCompletedBanner";
import { Section } from "views/applications-id-report/components/Section";
import { Stat } from "views/applications-id-report/components/Stat";
import { InfoCircle } from "untitledui-js-base";
import { formatToCurrency } from "shared/utils";
import { ApplicantSettings } from "shared/types";
import { InsufficientCreditScore } from "./InsufficientCreditScore";
import { toFixedFloored } from "utils/toFixedFloored";

type AccountsSummaryProps = {
  creditScore?: number | null;
  pastDueAccount?: number | null;
  balanceOfCollections?: number;
  balanceOfUnpaidCollections?: number;
  bankruptcy?: number;
  bankruptcyYears?: number;
  bankruptcyCriteriaValue?: string;
  foreclosureAccounts?: number;
  criteria?: CreditReport["criteria"];
  verificationId: number;
  completed_at?: string | null;
  isNoHit: boolean;
  isInsufficientData?: boolean;
  applicantName: string;
  pastDueAccountType: string;
  debtBalance?: number;
  screeningCriteria?: ApplicantSettings;
  vendor?: DataVendors;
};

export const CreditReportSummary = ({
  creditScore,
  pastDueAccount,
  balanceOfUnpaidCollections,
  balanceOfCollections,
  bankruptcy,
  bankruptcyYears,
  bankruptcyCriteriaValue,
  foreclosureAccounts,
  criteria,
  verificationId,
  completed_at,
  isNoHit,
  isInsufficientData,
  applicantName,
  debtBalance,
  pastDueAccountType,
  screeningCriteria,
  vendor
}: AccountsSummaryProps) => {
  const shouldDisplayNoData = isNoHit || isInsufficientData;

  const getPastDueAccounts = () => {
    if (shouldDisplayNoData) return "-";
    if (pastDueAccount === null || pastDueAccount === undefined) return "--";

    if (pastDueAccountType === "Percentage")
      return `${toFixedFloored(pastDueAccount, 2)}%`;

    return String(pastDueAccount);
  };

  // Intentionally mixed return type -- criteria display requires Stat's 'value' to be a string
  const getCreditScoreValue = () => {
    if (shouldDisplayNoData) return "-";
    if (creditScore === 1) return <InsufficientCreditScore />;
    return String(creditScore ?? "--");
  };

  return (
    <Section sectionTitle="Credit report summary" maxWidth="100%">
      {completed_at ? (
        <ReportCompletedBanner
          verificationName={VerificationNames.CreditReport}
          completedAt={completed_at}
          reportId={String(verificationId)}
          vendor={vendor}
        />
      ) : null}

      {isNoHit ? (
        <div className="py-6 border-b border-alpha-40 -mx-6 mb-6 w-auto">
          <div className="flex w-full justify-between items-center mx-6 mb-6">
            <InfoCircle size="24" className="text-content-100 h-6 w-6 mr-4" />
            <p className="text-lg text-content-1000 font-bold mr-auto w-auto">
              {applicantName} has no credit history
            </p>
          </div>
          <div className=""></div>
        </div>
      ) : null}

      <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
        <Stat
          title="Credit score"
          value={getCreditScoreValue()}
          subtitle={isInsufficientData ? "Insufficient credit data" : undefined}
          width="240px"
          hasCriteria={!shouldDisplayNoData && !!criteria}
          criteriaValue={criteria?.credit_score}
          criteriaSuccess={(creditScore || 0) >= (criteria?.credit_score || 0)}
          criteriaImportance={screeningCriteria?.credit_score?.importance}
        />
        <Stat
          title="Past due accounts"
          value={getPastDueAccounts()}
          width="240px"
          hasCriteria={!shouldDisplayNoData && !!criteria}
          criteriaValue={
            criteria?.past_due_account +
            (pastDueAccountType === "Percentage" ? "%" : "")
          }
          criteriaSuccess={
            (pastDueAccount || 0) <= (criteria?.past_due_account || 0)
          }
          criteriaImportance={screeningCriteria?.past_due_account?.importance}
        />
        <div className="block md:hidden col-span-2 border-b border-alpha-40 my-4" />
        <Stat
          title="Balance past due"
          value={
            shouldDisplayNoData
              ? "-"
              : "$" + String(balanceOfUnpaidCollections ?? "--")
          }
          width="240px"
          hasCriteria={!shouldDisplayNoData && !!criteria}
          criteriaValue={criteria?.unpaid_collections_balance}
          formattedCriteriaValue={formatToCurrency({
            cents: (criteria?.unpaid_collections_balance || 0) * 100,
            currency: "USD"
          })}
          criteriaSuccess={
            (balanceOfUnpaidCollections || 0) <=
            (criteria?.unpaid_collections_balance || 0)
          }
          criteriaImportance={
            screeningCriteria?.unpaid_collections_balance?.importance
          }
        />
        <div className="hidden md:block md:col-span-3 border-b border-alpha-40 mb-2" />
        <Stat
          title="Balance of collections"
          value={
            shouldDisplayNoData
              ? "-"
              : "$" + String(balanceOfCollections ?? "--")
          }
          width="240px"
          hasCriteria={!shouldDisplayNoData && !!criteria}
          criteriaValue={criteria?.collections_balance}
          formattedCriteriaValue={formatToCurrency({
            cents: (criteria?.collections_balance || 0) * 100,
            currency: "USD"
          })}
          criteriaSuccess={
            (balanceOfCollections || 0) <= (criteria?.collections_balance || 0)
          }
          criteriaImportance={
            screeningCriteria?.collections_balance?.importance
          }
        />
        <div className="block md:hidden col-span-2 border-b border-alpha-40 my-4" />
        <Stat
          title="Bankruptcy"
          value={shouldDisplayNoData ? "-" : String(bankruptcy ?? "--")}
          width="240px"
          hasCriteria={!shouldDisplayNoData && !!criteria}
          criteriaYears={bankruptcyYears}
          criteriaValue={criteria?.bankruptcy_records}
          additionalCriteriaValue={bankruptcyCriteriaValue}
          criteriaSuccess={
            (bankruptcy || 0) <= (criteria?.bankruptcy_records || 0)
          }
          criteriaImportance={screeningCriteria?.bankruptcy_records?.importance}
        />
        <Stat
          title="Accounts in foreclosure"
          value={
            shouldDisplayNoData ? "-" : String(foreclosureAccounts ?? "--")
          }
          width="240px"
          hasCriteria={!shouldDisplayNoData && !!criteria}
          criteriaValue={criteria?.foreclosure_accounts}
          criteriaSuccess={
            (foreclosureAccounts || 0) <= (criteria?.foreclosure_accounts || 0)
          }
          criteriaImportance={
            screeningCriteria?.foreclosure_accounts?.importance
          }
        />
        <div className="block col-span-2 md:col-span-3 border-b border-alpha-40 my-2" />
        <Stat
          title="Debt balance"
          value={shouldDisplayNoData ? "-" : "$" + String(debtBalance ?? "--")}
          width="240px"
          className="mb-0 md:mb-4"
        />
      </div>
    </Section>
  );
};
